import {
  RiAlignLeft,
  RiArrowDownLine,
  RiArrowUpLine,
  RiEqualizerLine,
  RiLayoutRowLine,
} from "@remixicon/react";
import { Badge } from "@screener/uikit/components/badge";
import { Button } from "@screener/uikit/components/button";
import { ScrollFade } from "@screener/uikit/components/scroll-fade";
import {
  SegmentedControl,
  type SegmentedControlOption,
} from "@screener/uikit/components/segmented-control";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@screener/uikit/components/select";
import { Tag } from "@screener/uikit/components/tag";
import { cn } from "@screener/uikit/lib/utils";
import { type RefObject, useRef } from "react";
import {
  FILTER_TEXT,
  filtersButtonLabel,
  formatChipUsd,
  minimumsButtonParts,
  sortKeyLabel,
  stateLabel,
} from "@/lib/labels";
import type { CardRowOrder } from "./cards";
import {
  activeFilterCount,
  clearFilter,
  type DensityFilters,
  type DensitySort,
  type DensitySortKey,
  exchangeFilterActive,
  type ScreenMode,
} from "./derive";
import type { DisplayMinAgeState } from "./display-min-age";
import { DisplayMinAgeButton } from "./display-min-age-popover";
import {
  activeMinimumsCount,
  type DisplayMinimumsState,
  displayMinimumsUnavailable,
} from "./display-minimums";
import { exchangesChipText } from "./exchange-chip";
import { ChipContent, type ChipTone, chipToneClass, chipVariant } from "./filter-chip";
import { focusTargetAfterRemoval } from "./focus-handoff";
import { FreezeToggle, VisibleOfTotal } from "./screen-count-leaves";
import type { ScreenCountsStore } from "./screen-counts";
import type { DisplayMinAgeOutcome } from "./use-display-min-age";

/** Ключи сортировки §9 — один переключатель на все виды экрана (1.51, §64 п. 2). */
const SORT_KEY_OPTIONS: ReadonlyArray<{ value: DensitySortKey; label: string }> = [
  { value: "usd", label: sortKeyLabel("usd") },
  { value: "age", label: sortKeyLabel("age") },
  { value: "distance", label: sortKeyLabel("distance") },
];

/** Подписи ключей для закрытого `Select`: значение показывается без открытия списка. */
const SORT_KEY_ITEMS: Record<DensitySortKey, string> = {
  usd: sortKeyLabel("usd"),
  age: sortKeyLabel("age"),
  distance: sortKeyLabel("distance"),
};

/**
 * Варианты порядка строк §9 (1.23, ответ трейдера 23.09.2026) — только в
 * режиме карточек, рядом с сортировкой (design.md, решение 5).
 */
const ROW_ORDER_OPTIONS: ReadonlyArray<SegmentedControlOption<CardRowOrder>> = [
  {
    value: "book",
    label: <RiLayoutRowLine className="size-3.5" aria-hidden="true" />,
    ariaLabel: "Стакан",
    tooltip: "Стакан: аски над бидами по цене",
    iconOnly: true,
  },
  {
    value: "volume",
    label: <RiAlignLeft className="size-3.5" aria-hidden="true" />,
    ariaLabel: "По объёму",
    tooltip: "По объёму: от крупнейшей",
    iconOnly: true,
  },
];

export interface FilterPanelProps {
  filters: DensityFilters;
  onChange: (next: DensityFilters) => void;
  /** Личные минимумы объёма §59: кнопка «Мин. объём» называет число действующих или «недоступны». */
  displayMinimums: DisplayMinimumsState;
  /** Время до показа §82: при ошибке чтения рядом с кнопками стоит надпись «недоступно». */
  displayMinAge: DisplayMinAgeState;
  onSaveDisplayMinAge: (minAgeSec: number) => Promise<DisplayMinAgeOutcome>;
  onRemoveDisplayMinAge: () => Promise<DisplayMinAgeOutcome>;
  /** Перечитать время до показа — «Повторить» окна, когда чтение не удалось. */
  onReloadDisplayMinAge: () => void;
  /** Открыть окно всех минимумов; кнопка нажимается в любом состоянии списка. */
  onOpenDisplayMinimums: () => void;
  /**
   * Рынки бирж, которые вообще встречаются в потоке, ключами «<биржа>:<рынок>»
   * (§115): перечень приходит от демона.
   */
  venues: string[];
  /**
   * Счётчики экрана: сколько живых плотностей стоит за каждым значением
   * отбора (по стадиям и биржам, до применения фильтров) и «N из M». Числа
   * меняются почти каждый кадр, поэтому передаётся стабильное хранилище, а
   * рисуют их листья (`screen-count-leaves.tsx`): полоса целиком от них не
   * перерисовывается.
   */
  counts: ScreenCountsStore;
  open: boolean;
  onToggle: () => void;
  /** Вид экрана §9 из адреса — выбирается подпунктами панели, здесь только читается. */
  mode: ScreenMode;
  /**
   * Сортировка §9 — общая для всех режимов и управляется только здесь
   * (1.51, §64 п. 2): заголовки колонок таблицы и ситуаций её лишь показывают.
   */
  sort: DensitySort;
  onSortChange: (key: DensitySortKey) => void;
  /**
   * Порядок строк внутри карточки §9 (1.23) — тот же приём, что и у
   * сортировки: управляется здесь и только в режиме карточек.
   */
  rowOrder: CardRowOrder;
  onRowOrderChange: (rowOrder: CardRowOrder) => void;
  /**
   * Заморозка вида «Карточки» §77: кнопка рядом с «Фильтры», только в этом
   * виде. Число плотностей, прошедших фильтры после нажатия и ещё не
   * показанных, читает сама кнопка из `counts` (`ScreenCounts.newCount`).
   */
  frozen: boolean;
  onToggleFreeze: () => void;
}

/**
 * Кнопка «Мин. объём» (stage8-card-minimum, Д2): единственный вход в окно
 * личных минимумов. Никогда не `disabled` — из пустого состояния через неё
 * задаётся первый минимум. Акцентная при действующих минимумах, бледная с
 * пунктиром без них, предупреждающая при ошибке чтения.
 */
function MinimumsButton({
  displayMinimums,
  onOpen,
}: Pick<FilterPanelProps, "displayMinimums"> & { onOpen: () => void }) {
  const count = activeMinimumsCount(displayMinimums);
  const unavailable = displayMinimumsUnavailable(displayMinimums);
  const tone: ChipTone = unavailable ? "warning" : count !== null && count > 0 ? "set" : "unset";
  return (
    <Button variant={chipVariant(tone)} size="tag" onClick={onOpen} className={chipToneClass(tone)}>
      <ChipContent {...minimumsButtonParts(count, unavailable)} />
    </Button>
  );
}

type TagKey = "size" | "stages" | "exchanges" | "hideMm" | "onlyFavorites";

/** Какой фильтр снимает крестик метки. */
const TAG_FILTER: Record<TagKey, keyof DensityFilters> = {
  size: "minUsd",
  stages: "states",
  exchanges: "exchanges",
  hideMm: "hideMm",
  onlyFavorites: "onlyFavorites",
};

/** Метки, показанные сейчас, в порядке строки. */
function presentTags(filters: DensityFilters, venues: string[]): TagKey[] {
  const shown: Array<[TagKey, boolean]> = [
    ["size", filters.minUsd !== null],
    ["stages", filters.states.length > 0],
    ["exchanges", exchangeFilterActive(filters, venues)],
    ["hideMm", filters.hideMm],
    ["onlyFavorites", filters.onlyFavorites],
  ];
  return shown.filter(([, on]) => on).map(([key]) => key);
}

/**
 * Фишки заданных фильтров экрана и входы в окна минимумов и времени до показа.
 * Строка содержит только то, что отсекает данные (§110 п. 1): незаданные «ММ»
 * и «Избранное» не показываются, крестик снимает один фильтр (§110 п. 2).
 */
function FilterTags({
  filters,
  onChange,
  venues,
  displayMinimums,
  onOpenDisplayMinimums,
  displayMinAge,
  onSaveDisplayMinAge,
  onRemoveDisplayMinAge,
  onReloadDisplayMinAge,
  filtersButtonRef,
}: Pick<
  FilterPanelProps,
  | "filters"
  | "onChange"
  | "venues"
  | "displayMinimums"
  | "displayMinAge"
  | "onSaveDisplayMinAge"
  | "onRemoveDisplayMinAge"
  | "onReloadDisplayMinAge"
  | "onOpenDisplayMinimums"
> & { filtersButtonRef: RefObject<HTMLButtonElement | null> }) {
  const crossRefs = useRef<Partial<Record<TagKey, HTMLButtonElement | null>>>({});
  const present = presentTags(filters, venues);
  /**
   * Крестик снимает собственную метку, и браузер уронил бы фокус на `body`.
   * Фокус уходит заранее: на крестик следующей метки, иначе предыдущей, иначе
   * на кнопку «Фильтры». Метки стоят на постоянных местах, их узлы живут и после
   * снятия соседа.
   */
  const remove = (key: TagKey) => {
    const target = focusTargetAfterRemoval(
      present,
      present.filter((item) => item !== key),
      key,
    ) as TagKey | null;
    (target === null ? filtersButtonRef.current : crossRefs.current[target])?.focus();
    onChange(clearFilter(filters, TAG_FILTER[key]));
  };
  const crossRef = (key: TagKey) => (node: HTMLButtonElement | null) => {
    crossRefs.current[key] = node;
  };
  return (
    <>
      <MinimumsButton displayMinimums={displayMinimums} onOpen={onOpenDisplayMinimums} />
      {filters.minUsd !== null ? (
        <Tag
          label={FILTER_TEXT.sizeTag}
          onRemove={() => remove("size")}
          removeRef={crossRef("size")}
          removeLabel={FILTER_TEXT.removeSize}
        >
          {formatChipUsd(filters.minUsd)}
        </Tag>
      ) : null}
      {filters.states.length > 0 ? (
        <Tag
          label={FILTER_TEXT.stagesTag}
          onRemove={() => remove("stages")}
          removeRef={crossRef("stages")}
          removeLabel={FILTER_TEXT.removeStages}
        >
          {filters.states.map(stateLabel).join(", ")}
        </Tag>
      ) : null}
      {filters.exchanges !== null && exchangeFilterActive(filters, venues) ? (
        // Пустой выбор называется словами: иначе пустая таблица выглядела бы
        // так же, как «рынок спокоен».
        <Tag
          label={FILTER_TEXT.exchangesTag}
          onRemove={() => remove("exchanges")}
          removeRef={crossRef("exchanges")}
          removeLabel={FILTER_TEXT.removeExchanges}
        >
          {exchangesChipText(filters.exchanges, venues)}
        </Tag>
      ) : null}
      {filters.hideMm ? (
        <Tag
          label={FILTER_TEXT.hideMmTag.label}
          onRemove={() => remove("hideMm")}
          removeRef={crossRef("hideMm")}
          removeLabel={FILTER_TEXT.removeHideMm}
        >
          {FILTER_TEXT.hideMmTag.value}
        </Tag>
      ) : null}
      <DisplayMinAgeButton
        state={displayMinAge}
        onSave={onSaveDisplayMinAge}
        onRemove={onRemoveDisplayMinAge}
        onReload={onReloadDisplayMinAge}
      />
      {filters.onlyFavorites ? (
        <Tag
          label={FILTER_TEXT.onlyFavoritesTag.label}
          onRemove={() => remove("onlyFavorites")}
          removeRef={crossRef("onlyFavorites")}
          removeLabel={FILTER_TEXT.removeOnlyFavorites}
        >
          {FILTER_TEXT.onlyFavoritesTag.value}
        </Tag>
      ) : null}
    </>
  );
}

/**
 * Кнопка «Фильтры»: значок, слово и справа число заданных фильтров экрана
 * (§110 п. 3). Без заданных числа нет. Число входит в доступное имя. Уже
 * 420 px первой строки слово скрыто визуально.
 */
function FiltersButton({
  open,
  onToggle,
  activeCount,
  buttonRef,
}: {
  open: boolean;
  onToggle: () => void;
  activeCount: number;
  buttonRef: RefObject<HTMLButtonElement | null>;
}) {
  return (
    <Button
      ref={buttonRef}
      variant={open ? "secondary" : "outline"}
      onClick={onToggle}
      aria-label={filtersButtonLabel(activeCount)}
      aria-expanded={open}
      className={cn(
        "h-[26px] shrink-0 gap-1.5 rounded-sm border border-border px-2 text-xs font-medium text-foreground",
        !open && "bg-transparent dark:bg-transparent",
      )}
    >
      <RiEqualizerLine aria-hidden="true" className="size-3.5" />
      {/* На самой узкой строке (390 px с заморозкой и числом фильтров) слово
          не помещается вместе со счётчиком «N из M»: остаются значок и число,
          имя кнопки задано `aria-label`. */}
      <span className="@max-[420px]:sr-only">Фильтры</span>
      {activeCount > 0 ? (
        <Badge size="count" aria-hidden="true">
          {activeCount}
        </Badge>
      ) : null}
    </Button>
  );
}

/**
 * Две строки над панелью. Первая, без переноса: «Фильтры», заморозка,
 * разделитель, сортировка и справа «N из M» (счётчик виден всегда). Вторая:
 * фишки в одну линию, при нехватке места она прокручивается по горизонтали, а
 * у края, за которым есть непоказанное, затухает. Когда первой строке не
 * хватает ширины, сегменты сортировки заменяются выпадающим списком
 * (container query на первой строке).
 */
export function FilterBar({
  filters,
  onChange,
  displayMinimums,
  displayMinAge,
  onSaveDisplayMinAge,
  onRemoveDisplayMinAge,
  onReloadDisplayMinAge,
  onOpenDisplayMinimums,
  venues,
  counts,
  open,
  onToggle,
  mode,
  sort,
  onSortChange,
  rowOrder,
  onRowOrderChange,
  frozen,
  onToggleFreeze,
}: FilterPanelProps) {
  const filtersButtonRef = useRef<HTMLButtonElement>(null);
  return (
    <div className="flex flex-col">
      {/* Первая строка — контейнер для переключения вида сортировки. Порог
          620 px: строка в режиме карточек с сегментами сортировки занимает
          около 575 px (замер на 768 px: фильтры 92, заморозка 26, сортировка
          с направлением и порядком строк 346, «N из M» 48, плюс отступы), а
          ещё ~45 px запаса держат счётчик с большими числами. */}
      <div className="@container">
        <div className="flex min-h-[38px] min-w-0 flex-nowrap items-center gap-1.5 px-3">
          <FiltersButton
            open={open}
            onToggle={onToggle}
            activeCount={activeFilterCount(filters, venues)}
            buttonRef={filtersButtonRef}
          />
          {mode === "cards" ? (
            <FreezeToggle store={counts} frozen={frozen} onToggle={onToggleFreeze} />
          ) : null}
          {/* Разделитель стоит между двумя непустыми группами: «Фильтры +
              заморозка» слева и сортировка справа. */}
          <span aria-hidden="true" className="mx-1 h-4 w-px shrink-0 bg-border" />
          {/* Сортировка и счётчик строк видны без разворачивания панели (design.md
              решение 6, этап 6 решение 2); вид выбирается в боковой панели. */}
          {/* Сортировка §9 — одним переключателем во всех четырёх видах (1.51,
              §64 п. 2); заголовки колонок таблицы и ситуаций её только
              показывают стрелкой. */}
          <div className="flex shrink-0 items-center gap-1.5">
            <div className="hidden @min-[620px]:contents">
              <SegmentedControl
                size="sm"
                label="Сортировка"
                value={sort.key}
                // Повторный клик по уже выбранному ключу не имеет права молча
                // перевернуть направление — это отдельное действие кнопки со
                // стрелкой (review, блок B).
                onValueChange={(key) => {
                  if (key !== sort.key) {
                    onSortChange(key);
                  }
                }}
                options={SORT_KEY_OPTIONS}
              />
            </div>
            <div className="contents @min-[620px]:hidden">
              <Select
                items={SORT_KEY_ITEMS}
                value={sort.key}
                onValueChange={(key) => {
                  if (key !== null && key !== sort.key) {
                    onSortChange(key);
                  }
                }}
              >
                <SelectTrigger size="sm" aria-label="Сортировка" className="h-[26px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_KEY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="ghost"
              size="icon-xs"
              className="size-[26px] text-foreground"
              aria-label={
                sort.direction === "asc"
                  ? "Направление: по возрастанию — сменить"
                  : "Направление: по убыванию — сменить"
              }
              onClick={() => onSortChange(sort.key)}
            >
              {sort.direction === "asc" ? (
                <RiArrowUpLine className="size-3.5" />
              ) : (
                <RiArrowDownLine className="size-3.5" />
              )}
            </Button>
            {mode === "cards" ? (
              // Порядок строк §9 (1.23, ответ трейдера 23.09.2026) — общий для
              // всех карточек, показывается только в режиме карточек: у таблицы,
              // вида «по монете» и списка ситуаций строки карточки нет вовсе.
              <SegmentedControl
                size="sm"
                label="Порядок строк"
                value={rowOrder}
                onValueChange={onRowOrderChange}
                options={ROW_ORDER_OPTIONS}
              />
            ) : null}
          </div>
          <div data-slot="filter-bar-count" className="ml-auto shrink-0 pl-1.5">
            <VisibleOfTotal store={counts} />
          </div>
        </div>
      </div>
      {/* Вторая строка: фишки в одну линию, прокрутка без полосы, затухание у
          края с непоказанным. */}
      <ScrollFade className="px-3" contentClassName="gap-1 py-[7px] *:shrink-0 *:whitespace-nowrap">
        <FilterTags
          filters={filters}
          onChange={onChange}
          venues={venues}
          displayMinimums={displayMinimums}
          onOpenDisplayMinimums={onOpenDisplayMinimums}
          displayMinAge={displayMinAge}
          onSaveDisplayMinAge={onSaveDisplayMinAge}
          onRemoveDisplayMinAge={onRemoveDisplayMinAge}
          onReloadDisplayMinAge={onReloadDisplayMinAge}
          filtersButtonRef={filtersButtonRef}
        />
      </ScrollFade>
    </div>
  );
}
